import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, LifeBuoy, BookOpen, Mail, Radio, ExternalLink, CheckCircle2, Clock, XCircle, UserPlus } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import BrandMark from "@/components/BrandMark";
import GestionLink from "@/components/GestionLink";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS = {
  pending: { label: "Pendiente de aceptación", icon: Clock, cls: "bg-amber-100 text-amber-800" },
  accepted: { label: "Matriculado", icon: CheckCircle2, cls: "bg-green-100 text-green-800" },
  rejected: { label: "No aceptado", icon: XCircle, cls: "bg-red-100 text-red-700" },
};

export default function CampusPage() {
  const { user, logout } = useAuth();
  const [courses, setCourses] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [enrolling, setEnrolling] = useState(null);

  const loadCourses = async () => {
    try {
      const { data } = await axios.get(`${API}/courses`);
      setCourses(data);
    } catch {
      toast.error("No se pudieron cargar los cursos");
      setCourses([]);
    }
  };

  const loadEnrollments = async () => {
    if (!user || user.role === "guest") return;
    try {
      const { data } = await axios.get(`${API}/my-enrollments`, { withCredentials: true });
      setEnrollments(data);
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    if (user) {
      loadCourses();
      loadEnrollments();
    }
  }, [user]);

  const enroll = async (courseId) => {
    setEnrolling(courseId);
    try {
      const { data } = await axios.post(`${API}/courses/${courseId}/enroll`, {}, { withCredentials: true });
      setEnrollments((prev) => [data, ...prev]);
      toast.success("Solicitud de matrícula enviada. El equipo la revisará.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar la solicitud");
    } finally {
      setEnrolling(null);
    }
  };

  if (user === null) {
    return (
      <div className="min-h-screen bg-[#fffbf2] flex items-center justify-center">
        <div className="w-10 h-10 border-4 border-[#e65100] border-t-transparent rounded-full animate-spin" aria-label="Cargando" />
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;

  const statusFor = (courseId) => enrollments.find((e) => e.course_id === courseId);

  return (
    <div data-testid="campus-page" className="min-h-screen bg-[#fffbf2] text-[#0f2027]">
      <header className="bg-[#e65100] text-white px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <BrandMark className="w-9 h-9" />
            <div className="leading-none">
              <span className="block font-display text-sm tracking-wider uppercase">PROTECCIÓN CIVIL VIGO</span>
              <span className="block text-[10px] tracking-widest text-white/80 font-mono-tactical uppercase">
                Campus de Teleformación
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <GestionLink testId="campus-gestion-link" />
            <span className="hidden sm:block text-xs font-mono-tactical uppercase tracking-widest text-white/80" data-testid="campus-user-name">
              {user.name}
            </span>
            <button
              onClick={logout}
              data-testid="campus-logout-btn"
              className="flex items-center gap-2 bg-[#0f2027] px-4 py-2 text-xs font-mono-tactical uppercase tracking-wider font-semibold rounded hover:bg-[#162c35] transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" aria-hidden="true" />
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-16 space-y-12">
        <div className="space-y-3 border-b border-[#0f2027]/10 pb-8">
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded bg-[#e65100]/10 text-[#e65100] text-xs font-mono-tactical uppercase tracking-widest">
            <LifeBuoy className="w-3.5 h-3.5" aria-hidden="true" />
            {user.role === "guest" ? "Acceso de invitado" : "Área personal"}
          </span>
          <h1 className="font-display text-4xl md:text-6xl uppercase tracking-tight">Cursos Disponibles</h1>
          <p className="text-sm md:text-base text-[#0f2027]/70 max-w-xl">
            {user.role === "guest"
              ? "Estás explorando como persona invitada. Para matricularte, crea una cuenta desde la página de acceso."
              : "Bienvenid@ de nuevo. Solicita matrícula en los cursos que te interesen; el equipo las acepta desde la administración."}
          </p>
        </div>

        {courses === null ? (
          <div className="py-16 flex justify-center">
            <div className="w-10 h-10 border-4 border-[#e65100] border-t-transparent rounded-full animate-spin" aria-label="Cargando" />
          </div>
        ) : courses.length === 0 ? (
          <p className="text-sm text-[#0f2027]/60" data-testid="campus-no-courses">Todavía no hay cursos publicados.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {courses.map((c) => {
              const enr = statusFor(c.id);
              const st = enr ? STATUS[enr.status] : null;
              const StIcon = st?.icon;
              return (
                <article
                  key={c.id}
                  data-testid={`campus-course-${c.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="p-8 bg-white border-2 border-[#0f2027] rounded-lg shadow-[6px_6px_0px_#0f2027] space-y-6 flex flex-col justify-between"
                >
                  <div className="space-y-4">
                    <div className="flex items-center justify-between text-xs font-mono-tactical uppercase">
                      <span className="bg-[#e65100] text-white px-2.5 py-1 rounded font-bold">Activo</span>
                      {c.lessons && (
                        <span className="text-[#0f2027]/70 font-semibold flex items-center gap-1.5">
                          <BookOpen className="w-3.5 h-3.5" aria-hidden="true" />
                          {c.lessons}
                        </span>
                      )}
                    </div>
                    <h2 className="font-display text-2xl md:text-3xl uppercase text-[#0f2027]">{c.title}</h2>
                    <p className="text-sm text-[#0f2027]/80 leading-relaxed">{c.desc}</p>
                  </div>
                  <div className="pt-4 border-t border-[#0f2027]/10 space-y-3">
                    {c.instructors && (
                      <div className="text-xs font-mono-tactical text-[#0f2027]/70">
                        Profesorado: {c.instructors.split(",").map((s) => s.trim()).join(" · ")}
                      </div>
                    )}
                    {user.role === "guest" ? (
                      <Link
                        to="/login"
                        data-testid={`campus-register-cta-${c.id}`}
                        className="inline-flex items-center gap-2 text-xs font-mono-tactical uppercase font-bold text-[#e65100] hover:underline"
                      >
                        <UserPlus className="w-3.5 h-3.5" aria-hidden="true" />
                        Crea una cuenta para matricularte
                      </Link>
                    ) : st ? (
                      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded text-xs font-mono-tactical uppercase font-bold ${st.cls}`} data-testid={`campus-status-${c.id}`}>
                        <StIcon className="w-3.5 h-3.5" aria-hidden="true" />
                        {st.label}
                      </span>
                    ) : (
                      <button
                        onClick={() => enroll(c.id)}
                        disabled={enrolling === c.id}
                        data-testid={`campus-enroll-${c.id}`}
                        className="inline-flex items-center gap-2 bg-[#0f2027] text-white px-5 py-2.5 text-xs font-mono-tactical uppercase tracking-wider font-bold rounded hover:bg-[#e65100] transition-colors disabled:opacity-60"
                      >
                        <UserPlus className="w-3.5 h-3.5" aria-hidden="true" />
                        {enrolling === c.id ? "Enviando..." : "Solicitar matrícula"}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        )}

        <div className="p-8 bg-[#0f2027] text-[#fffbf2] rounded-lg flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="space-y-1">
            <h3 className="font-display text-2xl uppercase">¿Dudas sobre tu acceso?</h3>
            <p className="text-sm text-[#fffbf2]/70">Resolvemos encantados todas tus consultas.</p>
          </div>
          <a
            href="mailto:info@busquedaysalvamento.es"
            data-testid="campus-contact-email"
            className="flex items-center gap-2 bg-[#e65100] text-white px-6 py-3 text-xs font-mono-tactical uppercase tracking-widest font-bold rounded hover:bg-[#c2410c] transition-colors"
          >
            <Mail className="w-4 h-4" aria-hidden="true" />
            info@busquedaysalvamento.es
          </a>
        </div>
      </main>

      <footer className="bg-[#0f2027] text-[#fffbf2]/60 py-8 px-6 text-xs font-mono-tactical border-t border-white/10">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <span className="uppercase tracking-wider">Protección Civil Concello de Vigo</span>
          <span className="flex items-center gap-2">
            <Radio className="w-3.5 h-3.5 text-[#e65100]" aria-hidden="true" />
            Plataforma de Teleformación Oficial · Galicia
          </span>
        </div>
      </footer>
    </div>
  );
}
